import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trophy, ChevronRight, Sparkles, Zap, Plus, Trash2 } from "lucide-react";
import ConfettiBurst from "@/components/ConfettiBurst";
import ChampionTrailer from "@/components/ChampionTrailer";

function MatchCard({ label, m, canScore, onScore, highlight = false }) {
  const done = m.status === "completed" && m.home && m.away;
  return (
    <div
      className={`border ${highlight ? "border-[#F4D03F]" : "border-[#232330]"} bg-[#101015] p-4 min-w-[260px] relative`}
      data-testid={`bracket-match-${label}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase">{label}</div>
        {m.status === "completed" && (
          <div className="text-[9px] font-mono tracking-widest text-cyan-400 uppercase">FT</div>
        )}
        {m.status === "projected" && (
          <div className="text-[9px] font-mono tracking-widest text-gray-500 uppercase">projected</div>
        )}
      </div>
      {[["home", m.home, m.home_score, m.seed_home], ["away", m.away, m.away_score, m.seed_away]].map(([side, t, score, seed], i) => (
        <div
          key={side}
          className={`flex items-center justify-between py-2 ${i === 0 ? "border-b border-[#232330]" : ""} ${
            done && m.winner_team_id === t?.id ? "text-white" : "text-gray-400"
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {seed && <span className="text-[9px] font-mono text-gray-600 w-4">#{seed}</span>}
            <span className="inline-block w-1 h-5 shrink-0" style={{ backgroundColor: t?.logo_color || "#232330" }} />
            <span className="font-heading font-bold uppercase tracking-tight text-sm truncate">
              {t?.name || "TBD"}
            </span>
            {done && m.winner_team_id === t?.id && (
              <Sparkles className="w-3.5 h-3.5 text-[#F4D03F] shrink-0" />
            )}
          </div>
          <span className={`font-mono text-sm ${done ? "text-white font-bold" : "text-gray-600"}`}>
            {score ?? "–"}
          </span>
        </div>
      ))}
      {canScore && m.status !== "completed" && m.home && m.away && (
        <button
          onClick={() => onScore(m)}
          data-testid={`score-match-${m.id}`}
          className="w-full mt-3 text-[10px] font-mono tracking-widest uppercase border border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F] hover:text-white transition-colors py-1.5"
        >
          Score match
        </button>
      )}
    </div>
  );
}

export default function Bracket() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [brackets, setBrackets] = useState([]);
  const [activeId, setActiveId] = useState("legacy");
  const [data, setData] = useState(null);
  const [scoreFor, setScoreFor] = useState(null);
  const [hs, setHs] = useState(0);
  const [as_, setAs] = useState(0);
  const [burst, setBurst] = useState(false);
  const [champions, setChampions] = useState([]);
  const [players, setPlayers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [autoSeedOpen, setAutoSeedOpen] = useState(false);
  const [autoSeed, setAutoSeed] = useState({ division_id: "", size: 4, force: false });
  const [bkForm, setBkForm] = useState({ name: "", division_id: "__all__", size: 4 });
  const [publishOpen, setPublishOpen] = useState(false);
  const [pubForm, setPubForm] = useState({ starts_at: "", interval_minutes: 1440, default_map: "Wheelspin" });

  const isLegacy = activeId === "legacy";
  const activeBracket = brackets.find((b) => b.id === activeId);

  const loadList = () =>
    api.get("/brackets").then((r) => setBrackets(r.data || [])).catch(() => setBrackets([]));

  const load = () => {
    if (isLegacy) {
      return api.get("/bracket").then((r) => {
        setData((prev) => {
          if (r.data?.champion && !prev?.champion) setBurst(true);
          return r.data;
        });
      });
    }
    return api.get(`/brackets/${activeId}`).then((r) => {
      setData((prev) => {
        if (r.data?.champion && !prev?.champion) setBurst(true);
        return r.data;
      });
    }).catch(() => setData(null));
  };

  useEffect(() => {
    loadList();
    api.get("/champions").then((r) => setChampions(r.data || [])).catch(() => {});
    api.get("/players").then((r) => setPlayers(r.data || [])).catch(() => {});
    api.get("/divisions").then((r) => setDivisions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const generate = async () => {
    try {
      if (isLegacy) {
        await api.post("/bracket/generate");
      } else {
        await api.post(`/admin/brackets/${activeId}/generate`);
      }
      toast.success("Bracket seeded.");
      load();
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to seed");
    }
  };

  const createBracket = async () => {
    if (!bkForm.name.trim()) { toast.error("Name required."); return; }
    try {
      const body = {
        name: bkForm.name.trim(),
        size: Number(bkForm.size),
        division_id: bkForm.division_id === "__all__" ? null : bkForm.division_id,
      };
      const { data } = await api.post("/admin/brackets", body);
      toast.success("Bracket created.");
      setCreateOpen(false);
      setBkForm({ name: "", division_id: "__all__", size: 4 });
      await loadList();
      setActiveId(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeBracket = async (b) => {
    if (!window.confirm(`Delete bracket "${b.name}"? This wipes its matches.`)) return;
    try {
      await api.delete(`/admin/brackets/${b.id}`);
      toast.success("Bracket removed.");
      if (activeId === b.id) setActiveId("legacy");
      await loadList();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const autoSeedPlayoffs = async () => {
    if (!autoSeed.division_id) { toast.error("Pick a division."); return; }
    try {
      const body = { size: Number(autoSeed.size), force: !!autoSeed.force };
      const { data } = await api.post(`/admin/divisions/${autoSeed.division_id}/playoff-seed`, body);
      toast.success(`Playoffs seeded — ${data.matches_created} matches, ${data.size} seeds.`);
      setAutoSeedOpen(false);
      setAutoSeed({ division_id: "", size: 4, force: false });
      await loadList();
      setActiveId(data.bracket_id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.message?.includes("Regular season not complete")) {
        if (window.confirm(`${detail.message}\n\nCompleted: ${detail.completed}/${detail.total}. Seed anyway?`)) {
          setAutoSeed((s) => ({ ...s, force: true }));
          // Retry with force
          try {
            const { data } = await api.post(`/admin/divisions/${autoSeed.division_id}/playoff-seed`,
              { size: Number(autoSeed.size), force: true });
            toast.success(`Playoffs seeded (forced) — ${data.matches_created} matches.`);
            setAutoSeedOpen(false);
            await loadList();
            setActiveId(data.bracket_id);
          } catch (ee) { toast.error(ee.response?.data?.detail?.message || ee.response?.data?.detail || "Failed"); }
        }
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Failed");
    }
  };

  const publishBracket = async () => {
    if (isLegacy) { toast.error("Publish only supports the multi-bracket engine."); return; }
    if (!pubForm.starts_at) { toast.error("Pick a start date/time."); return; }
    try {
      const body = {
        starts_at: new Date(pubForm.starts_at).toISOString(),
        interval_minutes: Number(pubForm.interval_minutes),
        default_map: pubForm.default_map,
      };
      const { data } = await api.post(`/admin/brackets/${activeId}/publish`, body);
      toast.success(`Published ${data.created} fixtures — check the Schedule.`);
      setPublishOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitScore = async () => {
    if (Number(hs) === Number(as_)) { toast.error("Knockouts can't end level."); return; }
    try {
      await api.post(`/bracket/${scoreFor.id}/score`, {
        home_score: Number(hs), away_score: Number(as_),
      });
      toast.success(scoreFor.round === "Final" ? "Grand Final locked. Ring ceremony initiated." : "Winner advances.");
      setScoreFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!data) return <div className="text-gray-500">Loading bracket…</div>;

  const generated = isLegacy ? data.generated : activeBracket?.generated;
  const sfs = data.semifinals || [];
  const final = data.final;
  const champion = data.champion;
  const projected = data.projected_champion;
  // Latest champion record matching the current bracket winner (for trailer)
  const trailerChamp = champion
    ? champions.find((c) => c.winner_team_id === champion.id) || null
    : null;
  const trailerMvp = trailerChamp && players.find((p) => p.id === trailerChamp.mvp_player_id);

  return (
    <div className="space-y-10" data-testid="bracket-root">
      <ConfettiBurst active={burst} onDone={() => setBurst(false)} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[#F4D03F] font-mono tracking-[0.3em] text-xs mb-2">/ KNOCKOUT STAGE</div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Playoff Brackets</h1>
          <p className="text-gray-400 mt-2 max-w-xl">
            Run as many brackets as you have divisions or playoffs. Each seeds from its own division standings.
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              onClick={() => setAutoSeedOpen(true)}
              data-testid="auto-seed-playoffs"
              className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold"
            >
              <Zap className="w-4 h-4 mr-2" /> Auto-seed playoffs
            </Button>
          )}
          {isAdmin && (
            <Button
              onClick={() => setCreateOpen(true)}
              data-testid="new-bracket"
              className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest font-bold"
            >
              <Plus className="w-4 h-4 mr-2" /> New bracket
            </Button>
          )}
          {isAdmin && !isLegacy && (
            <Button
              onClick={() => setPublishOpen(true)}
              data-testid="publish-bracket"
              className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Publish
            </Button>
          )}
          {isAdmin && (
            <Button
              onClick={generate}
              data-testid="generate-bracket"
              className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold"
            >
              <Zap className="w-4 h-4 mr-2" /> {(isLegacy ? data?.generated : activeBracket?.generated) ? "Re-seed" : "Seed"}
            </Button>
          )}
        </div>
      </div>

      {/* Bracket tabs */}
      <div className="flex flex-wrap gap-2" data-testid="bracket-tabs">
        <button
          onClick={() => setActiveId("legacy")}
          data-testid="bracket-tab-legacy"
          className={`px-3 py-1.5 border text-[10px] font-mono uppercase tracking-widest ${
            isLegacy ? "border-[#F4D03F] text-[#F4D03F]" : "border-[#232330] text-gray-500 hover:text-white"
          }`}
        >
          Season Playoff
        </button>
        {brackets.map((b) => {
          const dv = divisions.find((d) => d.id === b.division_id);
          return (
            <div key={b.id} className="flex items-center">
              <button
                onClick={() => setActiveId(b.id)}
                data-testid={`bracket-tab-${b.id}`}
                className={`px-3 py-1.5 border text-[10px] font-mono uppercase tracking-widest border-r-0 ${
                  activeId === b.id ? "border-[#B084FF] text-[#B084FF]" : "border-[#232330] text-gray-500 hover:text-white"
                }`}
              >
                {b.name}{dv ? ` · ${dv.name}` : ""} · {b.size}
              </button>
              {isAdmin && (
                <button
                  onClick={() => removeBracket(b)}
                  data-testid={`bracket-delete-${b.id}`}
                  className="border border-[#232330] px-2 py-1.5 text-gray-500 hover:border-red-500 hover:text-red-500"
                  title="Delete bracket"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {data && data.seeded < 4 ? (
        <div className="border border-[#232330] p-8 text-gray-500">Bracket unlocks once at least {activeBracket?.size || 4} teams are seeded.</div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-6 lg:gap-4 items-center overflow-x-auto">
          <div className="space-y-4">
            {(data?.quarterfinals || []).map((m, i) => (
              <MatchCard
                key={m.id || `qf-${i}`}
                label={`QF · ${m.seed_home || "?"}v${m.seed_away || "?"}`}
                m={m}
                canScore={isAdmin && generated}
                onScore={(x) => { setScoreFor(x); setHs(0); setAs(0); }}
              />
            ))}
            {sfs.map((m, i) => (
              <MatchCard
                key={m.id || `sf-${i}`}
                label={`SF · ${m.seed_home || "?"}v${m.seed_away || "?"}`}
                m={m}
                canScore={isAdmin && (isLegacy ? data.generated : activeBracket?.generated)}
                onScore={(x) => { setScoreFor(x); setHs(0); setAs(0); }}
              />
            ))}
          </div>
          <ChevronRight className="hidden lg:block w-6 h-6 text-cyan-400" />
          <div>
            {final && (
              <MatchCard
                label="Grand Final"
                m={final}
                canScore={isAdmin && generated}
                onScore={(x) => { setScoreFor(x); setHs(0); setAs(0); }}
                highlight
              />
            )}
          </div>
          <ChevronRight className="hidden lg:block w-6 h-6 text-[#F4D03F]" />
          <div
            className={`border p-6 ${
              champion ? "border-[#F4D03F] bg-[#F4D03F]/10" : "border-[#F4D03F]/40 bg-[#F4D03F]/5"
            }`}
            data-testid="bracket-champion"
          >
            <Trophy className="w-6 h-6 text-[#F4D03F] mb-3" />
            <div className="text-[10px] font-mono tracking-widest text-[#F4D03F] uppercase mb-2">
              {champion ? "Champion" : "Projected Champion"}
            </div>
            <div
              className="font-heading font-black text-3xl uppercase tracking-tighter"
              style={{ color: (champion || projected)?.logo_color }}
            >
              {(champion || projected)?.name || "TBD"}
            </div>
            <div className="font-mono text-xs text-gray-500 mt-2">
              {champion
                ? "Ring ceremony complete · added to Trophy Room."
                : "Based on current form & goal diff."}
            </div>
          </div>
        </div>
      )}

      {/* Champion Trailer — surfaces the moment a champion is crowned */}
      {champion && (
        <ChampionTrailer
          team={champion}
          highlightUrl={trailerChamp?.highlight_url}
          ringNote={trailerChamp?.ring_note}
          mvp={trailerMvp}
          season={trailerChamp?.season}
          year={trailerChamp?.year}
        />
      )}

      {/* Iter51 · One-click playoff auto-seed dialog */}
      <Dialog open={autoSeedOpen} onOpenChange={setAutoSeedOpen}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Auto-seed playoffs</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
              Ranks the division by points then wins, and builds a knockout bracket in one click.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Division</Label>
              <select
                data-testid="auto-seed-division"
                value={autoSeed.division_id}
                onChange={(e) => setAutoSeed({ ...autoSeed, division_id: e.target.value })}
                className="w-full mt-2 bg-[#05050A] border border-[#232330] p-2 font-mono text-sm"
              >
                <option value="">— pick a division —</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Bracket size</Label>
              <div className="flex gap-2 mt-2">
                {[4, 8].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setAutoSeed({ ...autoSeed, size: n })}
                    data-testid={`auto-seed-size-${n}`}
                    className={`flex-1 py-2 border font-mono uppercase tracking-widest text-xs ${
                      autoSeed.size === n
                        ? "border-[#F4D03F] bg-[#F4D03F]/10 text-[#F4D03F]"
                        : "border-[#232330] text-gray-400 hover:border-white hover:text-white"
                    }`}
                  >
                    Top {n}
                  </button>
                ))}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                4 → Semis + Final · 8 → Quarters + Semis + Final
              </div>
            </div>
            <Button
              onClick={autoSeedPlayoffs}
              disabled={!autoSeed.division_id}
              data-testid="auto-seed-submit"
              className="w-full rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold disabled:opacity-50"
            >
              <Zap className="w-4 h-4 mr-2" /> Seed the bracket
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create bracket dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Create bracket</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Name</Label>
              <Input
                data-testid="new-bracket-name"
                value={bkForm.name}
                onChange={(e) => setBkForm({ ...bkForm, name: e.target.value })}
                placeholder="Division A Playoffs"
                className="rounded-none bg-[#05050A] border-[#232330] mt-2"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Division (optional)</Label>
              <select
                data-testid="new-bracket-division"
                value={bkForm.division_id}
                onChange={(e) => setBkForm({ ...bkForm, division_id: e.target.value })}
                className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-2 p-2 text-sm"
              >
                <option value="__all__">All teams (cross-league)</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Bracket size</Label>
              <select
                data-testid="new-bracket-size"
                value={bkForm.size}
                onChange={(e) => setBkForm({ ...bkForm, size: Number(e.target.value) })}
                className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-2 p-2 text-sm"
              >
                <option value={4}>4-team (SF + Final)</option>
                <option value={8}>8-team (QF + SF + Final)</option>
              </select>
            </div>
            <Button
              onClick={createBracket}
              data-testid="new-bracket-submit"
              className="w-full rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest font-bold mt-2"
            >
              Create bracket
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish dialog — turns a generated bracket into fixtures on the Schedule */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Publish bracket to Schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">First match kickoff (UTC)</Label>
              <Input
                data-testid="publish-starts-at"
                type="datetime-local"
                value={pubForm.starts_at}
                onChange={(e) => setPubForm({ ...pubForm, starts_at: e.target.value })}
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Gap between matches (minutes)</Label>
              <Input
                data-testid="publish-interval"
                type="number" min={30} max={10080}
                value={pubForm.interval_minutes}
                onChange={(e) => setPubForm({ ...pubForm, interval_minutes: e.target.value })}
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                1440 = daily · 60 = hourly · 30 = back-to-back
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Default map</Label>
              <Input
                data-testid="publish-map"
                value={pubForm.default_map}
                onChange={(e) => setPubForm({ ...pubForm, default_map: e.target.value })}
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
            </div>
            <Button
              onClick={publishBracket}
              data-testid="publish-bracket-submit"
              className="rounded-none w-full bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
            >
              Publish to Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!scoreFor} onOpenChange={(o) => !o && setScoreFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              {scoreFor?.round} · {scoreFor?.home?.tag} vs {scoreFor?.away?.tag}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">{scoreFor?.home?.tag}</Label>
              <Input
                data-testid="bracket-home-score" type="number" min={0} value={hs}
                onChange={(e) => setHs(e.target.value)}
                className="rounded-none bg-[#05050A] border-[#232330] font-mono text-2xl mt-2"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">{scoreFor?.away?.tag}</Label>
              <Input
                data-testid="bracket-away-score" type="number" min={0} value={as_}
                onChange={(e) => setAs(e.target.value)}
                className="rounded-none bg-[#05050A] border-[#232330] font-mono text-2xl mt-2"
              />
            </div>
          </div>
          <Button
            onClick={submitScore}
            data-testid="confirm-bracket-score"
            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold mt-2"
          >
            {scoreFor?.round === "Final" ? "Lock final & crown champion" : "Publish & advance winner"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
